/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { GmailMessageDto } from '@features/email/interfaces/email-dto.interface';
import { GmailMessagesDto } from '@features/email/interfaces/emails-dto.interface';
import { Emails } from '@features/email/interfaces/emails.interface';
import { Email } from '@features/email/interfaces/email.interface';
import { EmailCreateDto } from '@features/email/interfaces/email-create-dto.interface';

@Injectable({ providedIn: 'root' })
export class GmailService {
  constructor(protected _http: HttpClient) {}

  public listMessages$(params: {
    maxResults?: number;
    q?: string;
    pageToken?: string;
    labelIds?: string[];
    listingUuid?: string;
  }): Observable<Emails> {
    let p = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) p = p.set(k, String(v));
    });

    return this._http.get<GmailMessagesDto>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/messages`, { params: p });
  }

  public batchGetThreads$(threadIds: string[]): Observable<Emails> {
    return this._http.post<Emails>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/threads/batch`, { threadIds });
  }

  public batchGetMessages$(messageIds: string[]): Observable<Emails> {
    return this._http.post<Emails>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/messages/batch`, { messageIds });
  }

  public getMessage$(id: string): Observable<Email> {
    return this._http.get<GmailMessageDto>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/messages/${id}`);
  }

  sendMessage$(p: EmailCreateDto): Observable<EmailCreateDto> {
    const normalize = (v?: string | string[]) =>
      v == null
        ? undefined
        : Array.isArray(v)
          ? v
          : v
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);

    const body = {
      to: normalize(p.to)!,
      subject: p.subject ?? '',
      text: p.text,
      html: p.html,
      cc: normalize(p.cc),
      bcc: normalize(p.bcc),
      replyTo: p.replyTo,
      from: p.from,
      fromName: p.fromName,
      threadId: p.threadId,
      attachments: p.attachments,
      inReplyTo: p.inReplyTo,
      references: p.references
    };

    return this._http.post<EmailCreateDto>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/messages/send`, body);
  }

  public markMessageRead$(id: string): Observable<any> {
    return this._http.post(`${environment.supplierApiGateway}/sup/integrations/google/gmail/messages/${id}/read`, {});
  }

  public markThreadRead$(id: string): Observable<any> {
    return this._http.post(`${environment.supplierApiGateway}/sup/integrations/google/gmail/threads/${id}/read`, {});
  }

  public listThreads$(params: { maxResults?: number; q?: string; pageToken?: string; customerEmail: string }): Observable<Emails> {
    let p = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) p = p.set(k, String(v));
    });
    return this._http.get<GmailMessagesDto>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/threads?`, { params: p });
  }

  public getThread$(threadId: string): Observable<any> {
    return this._http.get<any>(`${environment.supplierApiGateway}/sup/integrations/google/gmail/threads/${threadId}`);
  }

  public getAttachment$(messageId: string, attachment: { attachmentId: string; filename: string; mimeType: string }): Observable<Blob> {
    const params = new URLSearchParams({
      filename: attachment.filename,
      mimeType: attachment.mimeType
    });

    const url = `${environment.supplierApiGateway}/sup/integrations/google/gmail/${messageId}/attachments/${attachment.attachmentId}?${params.toString()}`;

    return this._http.get(url, { responseType: 'blob' });
  }
}
